import React, { useEffect, useRef, useState } from 'react';
import { PlayIcon, PauseIcon, ArrowUpRightIcon } from 'lucide-react';

// Branded SoundCloud player: the official widget iframe is kept as a HIDDEN audio
// engine (SoundCloud's white UI never shows); we render a FAR-skinned dark player
// on top of the Widget API. SoundCloud lazy-loads playlist metadata, so the track
// list is refreshed on READY and again on PLAY, and unloaded rows are hidden.

declare global {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  interface Window {
    SC?: any;
  }
}
let apiPromise: Promise<void> | null = null;
function loadApi(): Promise<void> {
  if (!apiPromise) {
    apiPromise = new Promise((resolve, reject) => {
      if (window.SC?.Widget) return resolve();
      const s = document.createElement('script');
      s.src = 'https://w.soundcloud.com/player/api.js';
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('sc api blocked'));
      document.head.appendChild(s);
    });
  }
  return apiPromise;
}
const clean = (t?: string) => (t || '').replace(/^Future Astronaut Records\s*-\s*/i, '');
const fmt = (ms: number) => {
  if (!ms) return '';
  const m = Math.floor(ms / 60000);
  const s = Math.floor(ms % 60000 / 1000);
  return `${m}:${String(s).padStart(2, '0')}`;
};
type Track = {
  idx: number;
  title: string;
  duration: number;
};
type Props = {
  src: string;
  open: string;
  title: string;
};
export function SoundCloudPlayer({
  src,
  open,
  title
}: Props) {
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const widgetRef = useRef<any>(null);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [cur, setCur] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [prog, setProg] = useState(0);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let dead = false;
    const to = setTimeout(() => {
      if (!widgetRef.current) setFailed(true);
    }, 6000);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const refresh = (w: any) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      w.getSounds((ss: any[]) => {
        if (dead) return;
        const rows = (ss || []).map((t, i) => ({
          idx: i,
          title: clean(t?.title),
          duration: t?.duration || 0
        })).filter(t => t.title);
        if (rows.length) setTracks(rows);
      });
    };
    loadApi().then(() => {
      if (dead || !frameRef.current || !window.SC?.Widget) return;
      const w = window.SC.Widget(frameRef.current);
      widgetRef.current = w;
      const E = window.SC.Widget.Events;
      w.bind(E.READY, () => {
        clearTimeout(to);
        refresh(w);
      });
      w.bind(E.PLAY, () => {
        if (dead) return;
        setPlaying(true);
        refresh(w);
        w.getCurrentSoundIndex((i: number) => {
          if (!dead && typeof i === 'number') setCur(i);
        });
      });
      w.bind(E.PAUSE, () => !dead && setPlaying(false));
      w.bind(E.FINISH, () => !dead && setPlaying(false));
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      w.bind(E.PLAY_PROGRESS, (e: any) => !dead && setProg(e.relativePosition || 0));
    }).catch(() => !dead && setFailed(true));
    return () => {
      dead = true;
      clearTimeout(to);
    };
  }, []);
  const current = tracks.find(t => t.idx === cur);
  return <div className="scp flex min-h-0 min-w-0 flex-1 flex-col gap-3" data-id="element-343" data-mp="2le62diob9uhq">
      <iframe ref={frameRef} title={title} src={src} allow="autoplay" style={{
      position: 'absolute',
      width: 2,
      height: 2,
      opacity: 0,
      pointerEvents: 'none',
      border: 0
    }} data-id="element-344" data-mp="2ghfon1zzbazh" />
      {/* now playing */}
      <div className="flex items-center gap-3" data-id="element-345" data-mp="2q78wbrx035yg">
        <button type="button" aria-label="Play / pause" onClick={() => widgetRef.current?.toggle()} className="flex h-11 w-11 flex-none items-center justify-center rounded-full text-white shadow-neon transition-transform duration-150 ease-expo active:scale-95" style={{
        background: 'linear-gradient(135deg, #8b5cff, #c86bff)'
      }} data-id="element-346" data-mp="345p4zoefb6j8">
          {playing ? <PauseIcon className="h-4.5 w-4.5" aria-hidden="true" fill="currentColor" data-id="element-347" data-mp="3s2le4qv057ar" /> : <PlayIcon className="h-4.5 w-4.5" aria-hidden="true" fill="currentColor" data-id="element-348" data-mp="u7n2aouadep" />}
        </button>
        <div className="min-w-0 flex-1" data-id="element-349" data-mp="30bysfh5q3fur">
          <p className="truncate text-[0.92rem] font-semibold text-white" data-id="element-350" data-mp="34bzkw5pnprkt">
            {failed ? 'Open the playlist on SoundCloud ↗' : current ? current.title : tracks[0] ? tracks[0].title : 'Loading tracks…'}
          </p>
          <p className="mt-0.5 font-tech text-[0.58rem] uppercase tracking-[0.22em] text-white/40" data-id="element-351" data-mp="3eafb86gv0wt9">
            SoundCloud playlist
          </p>
        </div>
        <a href={open} target="_blank" rel="noopener noreferrer" aria-label="Open on SoundCloud" className="flex h-8 w-8 flex-none items-center justify-center rounded-lg border border-white/10 text-white/55 transition-colors duration-200 hover:border-cyan/50 hover:text-cyan" data-id="element-352" data-mp="on7l3weo2jpe">
          <ArrowUpRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-353" data-mp="24bnq113bsmze" />
        </a>
      </div>
      {/* progress */}
      <div className="h-[3px] overflow-hidden rounded-full bg-white/10" data-id="element-354" data-mp="1zc16yb0tgcio">
        <i className="block h-full" style={{
        width: `${prog * 100}%`,
        background: 'linear-gradient(100deg, #37e5ff 0%, #c86bff 48%, #8b5cff 100%)',
        transition: 'width .3s linear'
      }} data-id="element-355" data-mp="1vc0kq47h6x12" />
      </div>
      {/* tracklist */}
      <ol className="scp-list min-h-0 flex-1 list-none overflow-y-auto border-t border-white/10 p-0" data-id="element-356" data-mp="1gegn5icws44x">
        {tracks.map(t => <li key={t.idx} onClick={() => widgetRef.current?.skip(t.idx)} className={`flex cursor-pointer items-center gap-2.5 border-b border-white/5 px-1 py-2 transition-colors duration-150 hover:bg-violet/10 ${t.idx === cur ? 'text-cyan' : ''}`} data-id="element-357" data-mp="3fq18fqfb8a81">
            <span className={`w-5 flex-none font-tech text-[0.6rem] ${t.idx === cur ? 'text-cyan' : 'text-white/35'}`} data-id="element-358" data-mp="1duk8sx5fgitj">
              {String(t.idx + 1).padStart(2, '0')}
            </span>
            <span className={`min-w-0 flex-1 truncate text-[0.8rem] ${t.idx === cur ? 'text-cyan' : 'text-white/75'}`} data-id="element-359" data-mp="1450pjqdsymy5">
              {t.title}
            </span>
            <span className="flex-none font-tech text-[0.62rem] text-white/35" data-id="element-360" data-mp="1wsmoumfzz5f7">{fmt(t.duration)}</span>
          </li>)}
        {!tracks.length && !failed ? <li className="px-1 py-2 text-[0.8rem] text-white/40" data-id="element-361" data-mp="1islrvi2usi50">Loading tracklist…</li> : null}
      </ol>
    </div>;
}